import { Component, type ErrorInfo, type ReactNode } from 'react';

type Props = { children: ReactNode };
type State = { error: Error | null; stack: string | null };

/**
 * Filet de sécurité au rendu.
 *
 * Sans lui, une exception dans un composant démonte tout l'arbre React et
 * laisse une page BLANCHE : l'utilisateur constate que « rien ne s'affiche »,
 * sans le moindre indice, et le développeur doit deviner. L'erreur existait
 * pourtant, elle n'était visible que dans la console du navigateur.
 *
 * Ici, elle s'affiche à l'écran, avec de quoi la rapporter et de quoi
 * repartir — le reste de l'application n'a pas à en souffrir.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, stack: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Conservé aussi dans la console : l'affichage est pour l'utilisateur,
    // la trace complète est pour le diagnostic.
    console.error('Erreur de rendu', error, info);
    this.setState({ stack: info.componentStack ?? null });
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div style={{ padding: 32, maxWidth: 820, margin: '0 auto' }}>
        <h2 style={{ fontSize: 20, fontWeight: 800, marginBottom: 6 }}>
          Cet écran n'a pas pu s'afficher
        </h2>
        <p style={{ color: 'var(--ink-3)', fontWeight: 600, marginTop: 0 }}>
          Le reste de l'application reste utilisable. Le détail ci-dessous permet de corriger.
        </p>

        <pre
          style={{
            background: 'var(--panel-2)',
            border: '1px solid var(--line)',
            borderRadius: 10,
            padding: 14,
            font: '600 12px/1.5 var(--mono)',
            color: 'var(--alert-ink)',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {this.state.error.message}
          {this.state.stack ? `\n${this.state.stack.split('\n').slice(0, 6).join('\n')}` : ''}
        </pre>

        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <button className="btn primary" type="button" onClick={() => window.location.reload()}>
            Recharger la page
          </button>
          <button
            className="btn"
            type="button"
            onClick={() => this.setState({ error: null, stack: null })}
          >
            Réessayer sans recharger
          </button>
        </div>
      </div>
    );
  }
}
